import React, { useMemo, useState } from 'react';
import { Switch, useRouteMatch } from 'react-router-dom';

import { Brand } from 'apis/enterprise/types';
import { MerchantDetail } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { RouteWhenAuthenticated } from 'common/components';
import Loading from 'core/Loading';
import PageLoadError from 'core/PageLoadError';
import { useBrandContext } from 'modules/brand/context/BrandContext';
import useGetBrandVenues from 'modules/brand/hooks/useGetBrandVenues';
import { VatCategoriesParamsContextProvider } from 'modules/menu/components/VatCategories';
import { useMerchantContext } from 'modules/merchant/MerchantContext';

import { ScopeContextProvider } from './contexts/ScopeContext';
import { useIsSettingsPageEnabled } from './hooks/useIsSettingsPageEnabled';
import { SharedMenuRootPageV2 } from './pages/SharedMenuRootPageV2';
import { SharedMenuSettingsPage } from './pages/SharedMenuSettingsPage';
import { OfferingPlatformScope } from './types';

type SharedMenuRoutesProps = {
  country: Country;
  scope: OfferingPlatformScope;
  venues: Venue[];
  brand?: Brand;
  merchant?: MerchantDetail;
};

export const BrandSharedMenuRoot = () => {
  const { brand } = useBrandContext();
  if (!brand) {
    throw new Error('No brand in context, this should not happen!');
  }

  const { venues, error: brandVenuesError, isLoading: loadingVenues } = useGetBrandVenues({ brand });

  const scope: OfferingPlatformScope = { type: 'brand', id: brand?.id };

  const vatCategoriesParams = useMemo(() => ({ country: brand.country as Country }), [brand.country]);

  if (brandVenuesError) {
    return <PageLoadError />;
  }

  if (loadingVenues) {
    return <Loading />;
  }

  return (
    <VatCategoriesParamsContextProvider value={vatCategoriesParams}>
      <SharedMenuRoutes country={brand.country as Country} scope={scope} venues={venues} brand={brand} />
    </VatCategoriesParamsContextProvider>
  );
};

export const MerchantSharedMenuRoot = () => {
  const { merchant, merchantVenues } = useMerchantContext();

  const scope: OfferingPlatformScope = { type: 'merchant', id: merchant.itemid.$oid };

  const vatCategoriesParams = useMemo(
    () => ({ country: merchant.country, merchantAgreementParty: merchant.agreement_party_slug }),
    [merchant],
  );

  return (
    <VatCategoriesParamsContextProvider value={vatCategoriesParams}>
      <SharedMenuRoutes country={merchant.country} scope={scope} venues={merchantVenues} merchant={merchant} />
    </VatCategoriesParamsContextProvider>
  );
};

export const SharedMenuRoutes = ({ country, scope, venues, brand, merchant }: SharedMenuRoutesProps) => {
  const { path, url } = useRouteMatch();
  const [isCreateSelectionModalOpen, setIsCreateSelectionModalOpen] = useState(false);
  const isSettingsPageEnabled = useIsSettingsPageEnabled();

  return (
    <ScopeContextProvider value={scope}>
      <Switch>
        {isSettingsPageEnabled && (
          <RouteWhenAuthenticated exact path={`${path}/settings`}>
            <SharedMenuSettingsPage rootUrl={url} />
          </RouteWhenAuthenticated>
        )}

        <RouteWhenAuthenticated path={path}>
          <SharedMenuRootPageV2
            country={country}
            venues={venues}
            brand={brand}
            merchant={merchant}
            isSettingsPageEnabled={isSettingsPageEnabled}
            isCreateSelectionModalOpen={isCreateSelectionModalOpen}
            setIsCreateSelectionModalOpen={setIsCreateSelectionModalOpen}
          />
        </RouteWhenAuthenticated>
      </Switch>
    </ScopeContextProvider>
  );
};
